import { useState, useMemo, useEffect, useRef } from "react";
import { useFilters } from "@/context/FiltersContext";
import useHome from "@/lib/useHome";
import usePersistentState from "@/lib/usePersistentState";
import { money, haversineKm, addedMoneyForEvents, eventMoneyBreakdown, dollarsPerMile, mountainTodayISO, moneyLabel } from "@/lib/ui";
import { EntryChips } from "@/components/EntryChips";
import { getChoices, pickPerf, toggleEnteredPerf, rodeoTouched } from "@/lib/choices";
import { Loader2, Search, Check, ChevronDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import MobileViewHeader from "@/components/mobile/MobileViewHeader";
import { MRodeoActions, MRodeoDialogs } from "@/components/mobile/rodeoDialogs";
import { PickCircle } from "@/components/PickCircle";
import TableTutorial from "@/components/TableTutorial";

const dotColor = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#3B82F6" : "#A855F7";
const dow = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" }) : "";
const dnum = (d) => d ? new Date(d + "T00:00:00").getDate() : "";

// Mobile Table (portrait): a date-timeline canvas up top + a pull-up tray of rodeo cards.
// Tap a rodeo -> it highlights (persists) and its performances light up across the dates.
export default function MobileTable() {
  const { matches, rodeos, selectRodeo, selectedRodeoId, clearSelection, sortBy, events, displayEvents, dateRange } = useFilters();
  const forcedSel = useMemo(() => {
    if (!selectedRodeoId) return null;
    const r = (rodeos || []).find((x) => x.id === selectedRodeoId);
    return r && !matches(r) ? r : null;
  }, [selectedRodeoId, rodeos, matches]);
  const home = useHome();
  const [choices, setChoices] = useState(getChoices());
  useEffect(() => { const s = () => setChoices(getChoices()); window.addEventListener("rp-choices-changed", s); return () => window.removeEventListener("rp-choices-changed", s); }, []);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [focusId, setFocusId] = useState(selectedRodeoId);
  useEffect(() => { if (selectedRodeoId) setFocusId(selectedRodeoId); }, [selectedRodeoId]);
  const pick = (r, p) => setChoices(pickPerf(r.id, p.id));
  // Selected day is shared with the Calendar (same persistent key) so picking a day there scrolls/highlights it here.
  const [selDay] = usePersistentState("rp_m_cal_seldate", null);
  const listRef = useRef(null);
  const todayISO = mountainTodayISO();
  const [pinId, setPinId] = useState(null); // only a SEARCHED rodeo pins to top; a tap stays put
  const [dayFilter, setDayFilter] = useState(null); // ISO date -> show only rodeos running that day
  const [dayPickerOpen, setDayPickerOpen] = useState(false);
  // Every rodeo date (past + future) for the day-picker dots.
  const markedDates = useMemo(() => {
    const s = new Set();
    (rodeos || []).forEach((r) => (r.performances || []).forEach((p) => p.date && s.add(p.date)));
    return s;
  }, [rodeos]);

  const base = useMemo(() => {
    const onDay = (r) => !dayFilter || (r.performances || []).some((p) => p.date === dayFilter);
    // Forward-looking ranges (this month, upcoming, all) are floored at TODAY so the list stays
    // fast and planning-focused. When the viewed range is entirely in the PAST (the athlete
    // stepped a month back), we drop the floor so past rodeos show and they can look back.
    const rangeIsPast = !!(dateRange.to && dateRange.to < todayISO);
    const hasFuture = (r) => (r.performances || []).some((p) => p.date && p.date >= todayISO);
    const src = (rodeos || []).filter((r) => onDay(r) && (rangeIsPast || hasFuture(r)) && matches(r));
    // A rodeo chosen from SEARCH (even outside the current filters) is shown AND pinned to the
    // top. A normal in-list tap only highlights in place — it must NOT reorder/jump the card.
    if (selectedRodeoId && !src.some((r) => r.id === selectedRodeoId)) {
      const sel = (rodeos || []).find((r) => r.id === selectedRodeoId);
      if (sel) src.push(sel);
    }
    return src
    .map((r) => ({ ...r, km: home && r.lat && r.lng ? Math.round(haversineKm(home, { lat: r.lat, lng: r.lng })) : Infinity }))
    .sort((a, b) => {
      if (pinId) {
        if (a.id === pinId && b.id !== pinId) return -1;
        if (b.id === pinId && a.id !== pinId) return 1;
      }
      if (sortBy === "money") return (b.added_money || 0) - (a.added_money || 0);
      if (sortBy === "near") return a.km - b.km;
      if (sortBy === "value") return (dollarsPerMile(b, home, events) ?? -1) - (dollarsPerMile(a, home, events) ?? -1);
      return ((a.performances?.[0]?.date) || "9999").localeCompare((b.performances?.[0]?.date) || "9999");
    });
  }, [rodeos, matches, home, choices, selectedRodeoId, pinId, sortBy, events, todayISO, dayFilter, dateRange]);

  const [searchOpen, setSearchOpen] = useState(false);
  const searchResults = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return [];
    return (rodeos || []).filter((r) => `${r.name} ${r.city} ${r.state}`.toLowerCase().includes(qq)).slice(0, 15);
  }, [q, rodeos]);
  const goToRodeo = (r) => { setFocusId(r.id); setPinId(r.id); selectRodeo(r); setSearchOpen(false); setQ(""); };

  const days = useMemo(() => {
    if (dayFilter) return [dayFilter]; // single-day view
    const from = dateRange.from, to = dateRange.to;
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    // Days inside the viewed window that actually have rodeos (+ today when in view).
    const set = new Set();
    base.forEach((r) => (r.performances || []).forEach((p) => { if (p.date && (!from || p.date >= from) && (!to || p.date <= to)) set.add(p.date); }));
    const rangeIsPast = !!(to && to < todayISO);
    if (!rangeIsPast && (!from || todayISO >= from) && (!to || todayISO <= to)) set.add(todayISO);
    const sparse = [...set].sort();

    // When the window is BOUNDED (a month / preset), fill the whole span so a month always
    // reads to its end (e.g. all the way to Oct 31) — not just up to the last rodeo. Forward
    // windows are floored at TODAY (planning view); a fully-past window shows in full.
    let start, end;
    if (from && to) {
      start = (!rangeIsPast && from < todayISO) ? todayISO : from;
      end = to;
    } else {
      // Unbounded ("All dates"): fall back to the data-driven min→max of rodeo days.
      if (!sparse.length) return [todayISO];
      start = sparse[0]; end = sparse[sparse.length - 1];
    }
    if (end < start) end = start;
    const span = Math.round((Date.parse(end + "T00:00:00") - Date.parse(start + "T00:00:00")) / 86400000);
    // An unusually wide window (>~15 months) stays sparse to avoid rendering thousands of tiles.
    if (span > 460) return sparse.length ? sparse : [todayISO];
    const out = [];
    const cur = new Date(start + "T00:00:00"), last = new Date(end + "T00:00:00");
    while (cur <= last) { out.push(iso(cur)); cur.setDate(cur.getDate() + 1); }
    return out;
  }, [base, todayISO, dayFilter, dateRange]);

  // Anchor the horizontal date strip to TODAY — whatever day it is sits at the LEFT (not
  // the middle/end). Keyed on the day-SET content (daysKey), NOT array identity, so picking
  // 1/2/entered — which recomputes `base`/`days` by reference but not content — never resets
  // the horizontal scroll (fixes "the table jumps back to the left and I lose my spot").
  const daysKey = days.join(",");
  useEffect(() => {
    const el = listRef.current; if (!el) return;
    let idx = days.findIndex((d) => d >= todayISO);
    if (idx < 0) idx = 0;
    el.scrollLeft = idx * 64;
  }, [daysKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vertically open the list AT today's first upcoming rodeo (past rodeos sit above, scroll
  // up to reach them; future below). Keyed on the row-SET (baseKey) so it re-anchors only
  // when filters change — never on a pick — and never fights an active selection.
  const firstUpcomingId = useMemo(() => {
    const f = base.find((r) => (r.performances || []).some((p) => p.date && p.date >= todayISO));
    return f ? f.id : null;
  }, [base, todayISO]);
  const baseKey = base.map((r) => r.id).join(",");
  useEffect(() => {
    if (selectedRodeoId || !firstUpcomingId) return;
    const t = setTimeout(() => {
      const scroller = listRef.current; if (!scroller) return;
      const el = scroller.querySelector(`[data-testid="m-ptable-row-${firstUpcomingId}"]`);
      if (el && el.offsetParent !== null) scroller.scrollTop = el.offsetTop;
    }, 60);
    return () => clearTimeout(t);
  }, [baseKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const countOn = (d) => base.filter((r) => (r.performances || []).some((p) => p.date === d)).length;
  const list = base;

  // Tap to select/highlight in place (no date-range move, no reorder). Tapping the SAME card again clears it.
  const selectCard = (r) => {
    setPinId(null); // a tap must never pin/reorder — stay where you are
    if (selectedRodeoId === r.id) { clearSelection(); setFocusId(null); return; }
    setFocusId(r.id); selectRodeo(r, { moveRange: false });
  };

  // Parity with Calendar/Map: when a rodeo is selected (from search or another view), bring its
  // row into view so the highlight is immediately visible instead of sitting off-screen.
  useEffect(() => {
    if (!selectedRodeoId) return;
    const t = setTimeout(() => {
      const els = document.querySelectorAll(`[data-testid="m-ptable-row-${selectedRodeoId}"], [data-testid="m-table-lsrow-${selectedRodeoId}"]`);
      els.forEach((el) => {
        if (el.offsetParent === null) return; // hidden (other orientation)
        const scroller = el.closest('[data-testid="m-table-plist"], [data-testid="m-table-grid-ls"]');
        if (!scroller) return;
        const er = el.getBoundingClientRect(), sr = scroller.getBoundingClientRect();
        if (er.top < sr.top || er.bottom > sr.bottom) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedRodeoId, base]);

  return (
    <div className="flex flex-col bg-[#0C1017] text-slate-100 h-[calc(100dvh_-_var(--hdr)_-_var(--ftr))] transition-[height] duration-200 overflow-hidden" data-testid="mobile-table">
      <TableTutorial />
      {searchOpen && (
        <div className="fixed inset-0 z-[3000] bg-[#0C1017] flex flex-col" data-testid="m-table-search-overlay">
          <div className="shrink-0 flex items-center gap-2 p-3 border-b border-white/10 bg-[#0F141B]" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a rodeo…" data-testid="m-table-search-input"
                className="w-full text-[16px] bg-white/5 border border-white/10 text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-3 py-2.5 focus:outline-none focus:border-[#C8000F]" />
            </div>
            <button onClick={() => { setSearchOpen(false); setQ(""); }} data-testid="m-table-search-cancel"
              className="shrink-0 px-3 py-2 text-[14px] font-semibold text-slate-300 active:text-white">Cancel</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {q.trim() ? (
              searchResults.length ? searchResults.map((r) => (
                <button key={r.id} data-testid={`m-table-search-result-${r.id}`} onClick={() => goToRodeo(r)}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 border-b border-white/5 active:bg-white/5">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dotColor(r) }} />
                  <span className="min-w-0">
                    <span className="block text-[15px] font-semibold text-white truncate">{r.name}</span>
                    <span className="block text-[12px] text-slate-400 truncate">{[r.city, r.state].filter(Boolean).join(", ")}</span>
                  </span>
                </button>
              )) : <div className="px-4 py-6 text-[13px] text-slate-500">No rodeos match “{q}”.</div>
            ) : (
              <div className="px-4 py-6 text-[13px] text-slate-500">Start typing a rodeo name, city or province…</div>
            )}
          </div>
        </div>
      )}
      <DayPicker open={dayPickerOpen} onClose={() => setDayPickerOpen(false)} value={dayFilter}
        onPick={(iso) => { setDayFilter(iso); setDayPickerOpen(false); }} onClear={() => { setDayFilter(null); setDayPickerOpen(false); }}
        marked={markedDates} todayISO={todayISO} />
      <MobileViewHeader
        onSearch={() => setSearchOpen(true)} searchLabel="Search rodeos, towns…" searchTestid="m-table-search-open"
        onPicker={() => setDayPickerOpen(true)} pickerActive={!!dayFilter} pickerTestid="m-table-daypicker-btn">
        {dayFilter && (
          <button onClick={() => setDayFilter(null)} data-testid="m-table-dayfilter-chip"
            className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold bg-[#1F6FEB]/15 text-[#5B9CFF] px-3 py-2 rounded-lg">
            <span className="truncate">Showing {new Date(dayFilter + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} only</span>
            <span className="shrink-0 flex items-center gap-1">· Show all <X className="w-3.5 h-3.5" /></span>
          </button>
        )}
        {forcedSel && (
          <button onClick={clearSelection} data-testid="m-table-clear-search" className="w-full flex items-center justify-center gap-1.5 text-[12px] font-semibold bg-[#C8000F]/15 text-[#F26D77] px-3 py-2 rounded-lg">
            <span className="truncate">“{forcedSel.name}” — outside your filters</span>
            <span className="shrink-0 flex items-center gap-1">· Clear <X className="w-3.5 h-3.5" /></span>
          </button>
        )}
      </MobileViewHeader>

      <div className="relative flex-1 min-h-0">
        {rodeos === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : (
          <>
          <div ref={listRef} className="absolute inset-0 overflow-auto pb-6 max-lg:landscape:hidden" data-testid="m-table-plist">
            {base.length === 0 ? (
              <div className="text-center py-16 text-slate-500 text-sm">No rodeos match your filters.</div>
            ) : (
              <div className="min-w-max">
                {base.map((r) => (
                  <PortraitRow key={r.id} r={r} ch={choices[r.id] || {}} days={days} selDay={selDay} todayISO={todayISO} selected={selectedRodeoId === r.id} onSelect={() => selectCard(r)} onPick={pick} onEntered={(rr, p) => setChoices(toggleEnteredPerf(rr.id, p.id))} setDlg={setDlg} events={displayEvents} />
                ))}
              </div>
            )}
          </div>

          {/* LANDSCAPE: synced grid — frozen rodeo column (left) + date columns; rows & dates both align, one shared scroll */}
          <div className="hidden max-lg:landscape:block absolute inset-0 overflow-auto" data-testid="m-table-grid-ls">
            <div className="min-w-max">
              <div className="flex sticky top-0 z-20">
                <div className="sticky left-0 z-30 shrink-0 bg-[#0F141B] border-b border-r border-white/10 px-2 py-1.5" style={{ width: 260 }}>
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <button type="button" onClick={() => setSearchOpen(true)} data-testid="m-table-search-open-ls"
                      className="w-full text-left rounded-lg border border-white/10 bg-white/5 text-slate-400 pl-8 pr-2 py-1.5 text-[12px] active:bg-white/10 truncate">Search rodeos, towns…</button>
                  </div>
                </div>
                {days.map((d) => (
                  <div key={d} className="shrink-0 bg-[#0F141B] border-b border-r border-white/10 flex flex-col items-center justify-center py-1" style={{ width: 76 }}>
                    <span className="text-[10px] font-bold uppercase text-slate-400">{dow(d)}</span>
                    <span className="text-base font-extrabold leading-none text-slate-100">{dnum(d)}</span>
                    <span className="text-[9px] text-slate-500 mt-0.5">{countOn(d)}</span>
                  </div>
                ))}
              </div>
              {list.length === 0 ? (
                <div className="px-4 py-10 text-slate-500 text-sm">No rodeos match.</div>
              ) : list.map((r) => {
                const ch = choices[r.id] || {};
                const hot = focusId === r.id;
                const amtV = addedMoneyForEvents(r, displayEvents).amount ?? (r.added_money || 0);
                const label = moneyLabel(r, displayEvents);
                const dpm = isFinite(r.km) && r.km > 0 && amtV ? Math.round(amtV / (r.km / 1.60934)) : null;
                return (
                  <div key={r.id} className={`flex border-b border-white/5 ${hot ? "bg-[#12233B]" : ""}`} data-testid={`m-table-lsrow-${r.id}`}>
                    <div className={`sticky left-0 z-10 shrink-0 px-2.5 py-2 border-r border-white/10 ${hot ? "bg-[#12233B] ring-1 ring-inset ring-[#3B82F6]" : "bg-[#0C1017]"}`} style={{ width: 260 }}>
                      <button onClick={() => selectCard(r)} data-testid={`m-table-lsselect-${r.id}`} className="text-left block w-full">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(r) }} />
                          <span className="font-display font-bold text-[13px] text-white leading-snug truncate">{r.name}</span>
                          <span className="ml-auto flex items-center gap-1 shrink-0">
                            {ch.pref1 && <span className="w-4 h-4 rounded-full bg-[#C8000F] text-white text-[9px] font-extrabold flex items-center justify-center">1</span>}
                            {ch.pref2 && <span className="w-4 h-4 rounded-full bg-[#0C1017] border border-white/30 text-white text-[9px] font-extrabold flex items-center justify-center">2</span>}
                            {Object.values(ch.entered || {}).some(Boolean) && <span className="w-4 h-4 rounded-full bg-[#16A34A] text-white flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={3} /></span>}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#F26D77] font-medium mt-0.5 truncate">{[r.city, r.state].filter(Boolean).join(", ")}{label ? <span className="text-[#5CC46A]"> · {label}</span> : null}{dpm ? <span className="text-[#5CC46A]"> · ${dpm.toLocaleString()}/mi</span> : null}</div>
                      </button>
                      <MRodeoActions r={r} setDlg={setDlg} className="mt-1.5" />
                    </div>
                    {days.map((d) => {
                      const perfs = (r.performances || []).filter((p) => p.date === d);
                      return (
                        <div key={d} className="shrink-0 px-1 py-2 border-r border-white/5 flex flex-col gap-1 items-center" style={{ width: 76 }}>
                          {perfs.map((p) => {
                            const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                            const ent = !!(ch.entered || {})[p.id];
                            return (
                              <div key={p.id} data-testid={`m-table-lsperf-${r.id}-${p.id}`}
                                className={`w-full rounded-lg border px-1 py-1 flex flex-col items-center gap-1 ${ent ? "bg-[#16A34A]/20 border-[#16A34A]/60" : num === 1 ? "bg-[#166534]/25 border-[#166534]/60" : "bg-white/5 border-white/10"}`}>
                                <span className="text-[9px] text-slate-400 leading-none">{p.time || "TBD"}</span>
                                <PickCircle num={num} entered={ent} size={18} onClick={() => pick(r, p)} onToggleEntered={() => setChoices(toggleEnteredPerf(r.id, p.id))} testid={`m-lspick-${r.id}-${p.id}`} />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          </>
        )}
      </div>
      <MRodeoDialogs dlg={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

// Portrait list item: name/badges/dropdown header pinned to the left (sticky), with the date strip below.
// The whole list is ONE native horizontal scroll container, so every row's strip is truly locked together
// (like the landscape grid) — no JS mirroring/lag. Header stays pinned left while dates scroll.
function PortraitRow({ r, ch = {}, days, selDay, todayISO, selected, onSelect, onPick, onEntered, setDlg, events }) {
  const [open, setOpen] = useState(false);
  const hasEntered = Object.values(ch.entered || {}).some(Boolean);
  const amtV = addedMoneyForEvents(r, events).amount ?? (r.added_money || 0);
  const label = moneyLabel(r, events);
  const dpm = isFinite(r.km) && r.km > 0 && amtV ? Math.round(amtV / (r.km / 1.60934)) : null;
  const perfByDay = useMemo(() => {
    const m = {};
    (r.performances || []).forEach((p) => { if (p.date) (m[p.date] = m[p.date] || []).push(p); });
    return m;
  }, [r]);
  return (
    <div className="flex flex-col border-b border-white/[0.08]" data-testid={`m-ptable-row-${r.id}`}>
      <div className={`sticky left-0 z-10 w-[100vw] pt-2.5 pr-3 pl-[9px] border-l-[3px] transition-colors ${selected ? "bg-[#12233B] border-[#3B82F6]" : "bg-[#0C1017] border-transparent"}`}>
        <div className="flex items-center gap-2">
          <button onClick={onSelect} data-testid={`m-ptable-select-${r.id}`} className="flex items-center gap-2 min-w-0 flex-1 text-left">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(r) }} />
            <div className="min-w-0">
              <div className="font-display font-bold text-[14px] text-white truncate leading-tight">{r.name}</div>
              <div className="text-[11px] text-[#F26D77] font-medium truncate">
              {[r.city, r.state].filter(Boolean).join(", ")}
              {label ? <span className="text-[#5CC46A]"> · {label}</span> : null}
              {dpm ? <span className="text-[#5CC46A]"> · ${dpm.toLocaleString()}/mi</span> : null}
            </div>
              <EntryChips r={r} dark className="mt-1" />
            </div>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            {ch.pref1 && <span className="w-5 h-5 rounded-full bg-[#C8000F] text-white text-[10px] font-extrabold flex items-center justify-center">1</span>}
            {ch.pref2 && <span className="w-5 h-5 rounded-full bg-[#0C1017] border border-white/30 text-white text-[10px] font-extrabold flex items-center justify-center">2</span>}
            {hasEntered && <span className="w-5 h-5 rounded-full bg-[#16A34A] text-white flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>}
          </div>
          <button onClick={() => setOpen((v) => !v)} data-testid={`m-ptable-menu-${r.id}`} aria-label="Rodeo actions"
            className={`shrink-0 p-1.5 rounded-lg border transition-colors ${open ? "bg-[#C8000F] border-[#C8000F] text-white" : "bg-white/5 border-white/10 text-slate-300"}`}>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
        {open && (
          <div className="mt-2 pb-1" data-testid={`m-ptable-actions-${r.id}`}>
            <MRodeoActions r={r} setDlg={setDlg} />
          </div>
        )}
      </div>
      <div className="flex gap-1.5 px-3 pt-2 pb-2.5" data-testid={`m-ptable-perfs-${r.id}`}>
        {days.map((d) => {
          const perfs = perfByDay[d];
          const active = !!perfs;
          const anyEnt = active && perfs.some((p) => (ch.entered || {})[p.id]);
          const anyP1 = active && perfs.some((p) => ch.pref1 === p.id);
          return (
            <div key={d} data-testid={active ? `m-ptable-perf-${r.id}-${d}` : undefined}
              className={`shrink-0 w-[58px] min-h-[62px] rounded-lg border px-1 py-1.5 flex flex-col items-center gap-1 ${d === selDay ? "ring-2 ring-[#C8000F] ring-offset-0" : d === todayISO ? "ring-1 ring-[#E6B35A]/70" : ""} ${!active ? "bg-white/[0.02] border-white/[0.06]" : anyEnt ? "bg-[#16A34A]/20 border-[#16A34A]/60" : anyP1 ? "bg-[#166534]/25 border-[#166534]/60" : "bg-white/5 border-white/10"}`}>
              <span className={`text-[9px] font-bold uppercase leading-none ${d === todayISO ? "text-[#E6B35A]" : active ? "text-slate-400" : "text-slate-600"}`}>{d === todayISO ? "Today" : dow(d)}</span>
              <span className={`text-[14px] font-extrabold leading-none ${d === todayISO ? "text-[#E6B35A]" : active ? "text-slate-100" : "text-slate-600"}`}>{dnum(d)}</span>
              {active ? perfs.map((p) => {
                const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                const ent = !!(ch.entered || {})[p.id];
                return (
                  <div key={p.id} className="flex flex-col items-center gap-0.5">
                    <span className="text-[8px] text-slate-500 leading-none">{p.time || "TBD"}</span>
                    <PickCircle num={num} entered={ent} size={18} onClick={() => onPick(r, p)} onToggleEntered={() => onEntered(r, p)} testid={`m-ptable-pick-${r.id}-${p.id}`} />
                  </div>
                );
              }) : <span className="text-[11px] text-slate-700 mt-1 leading-none">·</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Tap-a-day month calendar. Picking a day filters the Table to only rodeos running that day.
function DayPicker({ open, onClose, value, onPick, onClear, marked, todayISO }) {
  const [ym, setYm] = useState(() => { const d = new Date((value || todayISO) + "T00:00:00"); return { y: d.getFullYear(), m: d.getMonth() }; });
  useEffect(() => { if (open) { const d = new Date((value || todayISO) + "T00:00:00"); setYm({ y: d.getFullYear(), m: d.getMonth() }); } }, [open, value, todayISO]);
  if (!open) return null;
  const first = new Date(ym.y, ym.m, 1);
  const startDow = first.getDay();
  const daysIn = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);
  const isoOf = (d) => `${ym.y}-${String(ym.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthLabel = first.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  return (
    <div className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center" data-testid="m-table-daypicker">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full sm:max-w-sm bg-[#0F141B] border-t sm:border border-white/10 rounded-t-2xl sm:rounded-2xl p-4" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setYm(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))} data-testid="dp-prev" className="p-2 rounded-lg active:bg-white/10 text-slate-300"><ChevronLeft className="w-5 h-5" /></button>
          <div className="font-display font-bold text-white" data-testid="dp-label">{monthLabel}</div>
          <button onClick={() => setYm(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))} data-testid="dp-next" className="p-2 rounded-lg active:bg-white/10 text-slate-300"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-500 mb-1">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const iso = isoOf(d);
            const has = marked.has(iso);
            const isPast = iso < todayISO;
            const sel = iso === value;
            const isToday = iso === todayISO;
            return (
              <button key={i} onClick={() => onPick(iso)} data-testid={`dp-day-${iso}`}
                className={`aspect-square rounded-lg flex items-center justify-center text-sm font-semibold relative
                  ${sel ? "bg-[#1F6FEB] text-white" : has ? "bg-white/5 text-slate-100 active:bg-white/15" : isPast ? "text-slate-600 active:bg-white/10" : "text-slate-500 active:bg-white/10"}
                  ${isToday && !sel ? "ring-1 ring-[#E6B35A]" : ""}`}>
                {d}
                {has && !sel ? <span className={`absolute bottom-1 w-1 h-1 rounded-full ${isPast ? "bg-slate-500" : "bg-[#16A34A]"}`} /> : null}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClear} data-testid="dp-all-days" className="flex-1 py-2.5 rounded-lg bg-white/5 text-slate-200 text-sm font-semibold active:bg-white/10">All days</button>
          <button onClick={onClose} data-testid="dp-close" className="flex-1 py-2.5 rounded-lg bg-[#C8000F] text-white text-sm font-semibold">Close</button>
        </div>
      </div>
    </div>
  );
}
